import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import FontSize from "@/lib/tiptap/FontSize";
import { cn } from "@/lib/utils";
import "./RichTextEditor.css";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: string;
  maxHeight?: string;
  onReady?: (editor: unknown) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  simpleToolbar?: boolean;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className,
  minHeight = "150px",
  maxHeight,
  onBlur,
  onFocus,
  simpleToolbar = false,
}: RichTextEditorProps) {
  const [fontSize, setFontSize] = React.useState("16");
  const [fontFamily, setFontFamily] = React.useState("sans-serif");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TextStyle,
      FontFamily.configure({
        types: ['textStyle'],
      }),
      FontSize,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onBlur: () => onBlur?.(),
    onFocus: () => onFocus?.(),
    editorProps: {
      attributes: {
        class: "focus:outline-none p-3",
        style: `min-height: ${minHeight}; ${maxHeight ? `max-height: ${maxHeight};` : ""} overflow-y: auto;`,
      },
    },
  });

  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={cn("border border-input rounded-md shadow-xs overflow-hidden rich-text-editor", className)}>
      <div className="border-b bg-muted/30 p-2 flex flex-wrap gap-1 items-center">
        <Select
          value={fontFamily}
          onValueChange={(value) => {
            setFontFamily(value);
            editor.chain().focus().setFontFamily(value).run();
          }}
        >
          <SelectTrigger className="w-[140px] h-8 text-xs">
            <SelectValue placeholder="Font" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="sans-serif">Normal</SelectItem>
            <SelectItem value="Courier New">Courier New</SelectItem>
            <SelectItem value="cursive">Cursive</SelectItem>
            <SelectItem value="monospace">Monospace</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={fontSize}
          onValueChange={(value) => {
            setFontSize(value);
            editor.chain().focus().setFontSize(`${value}px`).run();
          }}
        >
          <SelectTrigger className="w-[80px] h-8 text-xs">
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="12">12px</SelectItem>
            <SelectItem value="14">14px</SelectItem>
            <SelectItem value="16">16px</SelectItem>
            <SelectItem value="18">18px</SelectItem>
            <SelectItem value="20">20px</SelectItem>
            <SelectItem value="24">24px</SelectItem>
            <SelectItem value="28">28px</SelectItem>
            <SelectItem value="32">32px</SelectItem>
          </SelectContent>
        </Select>
        {!simpleToolbar && (
          <>
            <div className="border-l mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={cn(
                "px-2 py-1 rounded text-sm font-medium hover:bg-muted",
                editor.isActive("heading", { level: 1 }) ? "bg-muted" : ""
              )}
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={cn(
                "px-2 py-1 rounded text-sm font-medium hover:bg-muted",
                editor.isActive("heading", { level: 2 }) ? "bg-muted" : ""
              )}
            >
              H2
            </button>
            <div className="border-l mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn(
                "px-2 py-1 rounded text-sm font-medium hover:bg-muted",
                editor.isActive("bold") ? "bg-muted" : ""
              )}
            >
              B
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn(
                "px-2 py-1 rounded text-sm italic hover:bg-muted",
                editor.isActive("italic") ? "bg-muted" : ""
              )}
            >
              I
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={cn(
                "px-2 py-1 rounded text-sm underline hover:bg-muted",
                editor.isActive("underline") ? "bg-muted" : ""
              )}
            >
              U
            </button>
            <div className="border-l mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={cn(
                "px-2 py-1 rounded text-sm hover:bg-muted",
                editor.isActive("bulletList") ? "bg-muted" : ""
              )}
            >
              • List
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={cn(
                "px-2 py-1 rounded text-sm hover:bg-muted",
                editor.isActive("orderedList") ? "bg-muted" : ""
              )}
            >
              1. List
            </button>
          </>
        )}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export default RichTextEditor;
